/**
 * Prüfung des Formulars „Event eintragen“ bzw. „Bearbeitung vorschlagen“ (30.10.2026).
 *
 * Vorher meldete das Web fehlende Pflichtfelder nur als Toast oben rechts („Bitte fülle die
 * Pflichtfelder aus: Titel, PLZ“), und wer weit unten stand, musste die Felder suchen. Jetzt
 * liefert die Prüfung je Feld eine Meldung; die Oberfläche markiert das Feld, schreibt den Satz
 * darunter und springt zum ersten. Die Regeln stehen hier, damit die App dieselben nimmt.
 *
 * Die Reihenfolge der Schlüssel ist die Reihenfolge im Formular: die erste Meldung ist das
 * Feld, zu dem gesprungen wird.
 */
export type EventVorschlagFeld =
  | 'title' | 'eventDate' | 'endDate' | 'postalCode' | 'city' | 'bundesland'
  | 'newOrganizerName' | 'submitterEmail';

export interface EventVorschlagEingabe {
  title: string;
  eventDate: string;
  endDate: string;
  permanent: boolean;
  postalCode: string;
  city: string;
  bundesland: string;
  submitterEmail: string;
  /** Nur geprüft, wenn ein neuer Veranstalter angelegt wird. */
  newOrganizerName?: string | null;
}

export const EVENT_VORSCHLAG_LABELS: Record<EventVorschlagFeld, string> = {
  title: 'Titel',
  eventDate: 'Datum',
  endDate: 'Bis',
  postalCode: 'PLZ',
  city: 'Ort',
  bundesland: 'Bundesland oder Land',
  newOrganizerName: 'Name des Veranstalters',
  submitterEmail: 'E-Mail',
};

export function pruefeEventVorschlag(f: EventVorschlagEingabe): Partial<Record<EventVorschlagFeld, string>> {
  const fehler: Partial<Record<EventVorschlagFeld, string>> = {};
  if (!f.title.trim()) fehler.title = 'Gib dem Event einen Namen.';
  if (!f.permanent && !f.eventDate) fehler.eventDate = 'Wann findet das Event statt?';
  if (!f.permanent && f.eventDate && f.endDate && f.endDate < f.eventDate) {
    fehler.endDate = 'Das Ende liegt vor dem Beginn.';
  }
  // Ein permanentes Event hat oft mehrere Einstiegspunkte: dann steht nur die Region fest.
  if (!f.permanent && !f.postalCode.trim()) fehler.postalCode = 'PLZ fehlt.';
  if (!f.permanent && !f.city.trim()) fehler.city = 'Ort fehlt.';
  if (!f.bundesland) fehler.bundesland = 'Bitte wählen.';
  if (f.newOrganizerName != null && !f.newOrganizerName.trim()) {
    fehler.newOrganizerName = 'Wie heißt der Veranstalter?';
  }
  // Freiwillig, aber wenn sie da ist, muss sie stimmen: sonst geht die Rückmeldung ins Leere.
  const mail = f.submitterEmail.trim();
  if (mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
    fehler.submitterEmail = 'Diese Adresse stimmt nicht. Prüf sie oder lass das Feld leer.';
  }
  return fehler;
}

/** „Titel und PLZ fehlen noch.“ für die Zeile über dem Formular. */
export function eventVorschlagZusammenfassung(fehler: Partial<Record<EventVorschlagFeld, string>>): string | null {
  const namen = (Object.keys(fehler) as EventVorschlagFeld[]).map(k => EVENT_VORSCHLAG_LABELS[k]);
  if (namen.length === 0) return null;
  if (namen.length === 1) return `Bitte prüf noch: ${namen[0]}.`;
  return `Bitte prüf noch: ${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}.`;
}
